import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { tasksAPI, projectsAPI } from '../services/api';
import { Task, UserRef } from '../types';

interface TaskModalProps {
  visible: boolean;
  onHide: () => void;
  projectId: string;
  task?: Task | null;
  onSave: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ visible, onHide, projectId, task, onSave }) => {
  const [users, setUsers] = useState<UserRef[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignees: [] as string[],
    status: 'todo' as 'todo' | 'in_progress' | 'done',
    priority: 'medium' as 'low' | 'medium' | 'high',
    deadline: null as Date | null,
    estimatedHours: 0,
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const [assigneesPanelOpen, setAssigneesPanelOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  useEffect(() => {
    if (visible) {
      loadUsers();
      if (task) {
        setFormData({
          name: task.name,
          description: task.description || '',
          assignees: task.assignees.map(a => a._id),
          status: task.status,
          priority: task.priority,
          deadline: task.deadline ? new Date(task.deadline) : null,
          estimatedHours: task.estimatedHours || 0,
          tags: task.tags || []
        });
      } else {
        setFormData({
          name: '',
          description: '',
          assignees: [],
          status: 'todo',
          priority: 'medium',
          deadline: null,
          estimatedHours: 0,
          tags: []
        });
      }
      setTagInput('');
    }
  }, [visible, task, projectId]);

  const loadUsers = async () => {
    try {
      // Učitaj samo članove tima za dati projekat
      const project = await projectsAPI.getById(projectId);
      setUsers(project.team || []);
    } catch (err: any) {
      console.error('Error loading project team members:', err);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return;
    }
    if (formData.assignees.length === 0) {
      return;
    }

    try {
      if (task) {
        await tasksAPI.update(task._id, {
          name: formData.name,
          description: formData.description,
          status: formData.status,
          priority: formData.priority,
          deadline: formData.deadline?.toISOString(),
          estimatedHours: formData.estimatedHours,
          tags: formData.tags,
          assignees: formData.assignees
        });
      } else {
        await tasksAPI.create({
          name: formData.name,
          description: formData.description,
          status: formData.status,
          priority: formData.priority,
          deadline: formData.deadline?.toISOString(),
          estimatedHours: formData.estimatedHours,
          tags: formData.tags,
          projectId,
          assignees: formData.assignees
        });
      }
      onSave();
      onHide();
    } catch (err: any) {
      console.error('Error saving task:', err);
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const statusOptions = [
    { label: 'Za rad', value: 'todo' },
    { label: 'U toku', value: 'in_progress' },
    { label: 'Završeno', value: 'done' }
  ];

  const priorityOptions = [
    { label: 'Nizak', value: 'low' },
    { label: 'Srednji', value: 'medium' },
    { label: 'Visok', value: 'high' }
  ];

  const userOptions = users.map(u => ({
    label: `${u.firstName} ${u.lastName}`,
    value: u._id
  }));

  const selectedAssignees = users.filter(u => formData.assignees.includes(u._id));
  const assigneesDisplayValue =
    selectedAssignees.length === 0
      ? 'Izaberite korisnike'
      : selectedAssignees.map(u => `${u.firstName} ${u.lastName}`).join(', ');

  const toggleAssignee = (userId: string) => {
    setFormData(prev => {
      const exists = prev.assignees.includes(userId);
      const assignees = exists
        ? prev.assignees.filter(id => id !== userId)
        : [...prev.assignees, userId];
      return { ...prev, assignees };
    });
  };

  return (
      <Dialog
        header={task ? 'Izmeni zadatak' : 'Dodaj zadatak'}
        visible={visible}
        style={{ width: '600px' }}
        contentStyle={{ maxHeight: '80vh', overflow: 'visible' }}
        onHide={onHide}
      footer={
        <div>
          <Button label="Otkaži" icon="pi pi-times" onClick={onHide} className="p-button-text" />
          <Button label={task ? 'Sačuvaj' : 'Kreiraj'} icon="pi pi-check" onClick={handleSubmit} />
        </div>
      }
      >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '70vh', overflow: 'auto' }}>
        <div>
          <label htmlFor="taskName" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Naziv zadatka <span style={{ color: 'red' }}>*</span>
          </label>
          <InputText
            id="taskName"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label htmlFor="taskDescription" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Opis
          </label>
          <InputTextarea
            id="taskDescription"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            style={{ width: '100%' }}
            rows={3}
          />
        </div>
        <div>
          <label htmlFor="taskAssignees" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Dodeljeni korisnici <span style={{ color: 'red' }}>*</span>
          </label>
          <div style={{ position: 'relative' }}>
            <div
              id="taskAssignees"
              onClick={() => setAssigneesPanelOpen(open => !open)}
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: 'white'
              }}
            >
              <span
                style={{
                  color: selectedAssignees.length === 0 ? '#9ca3af' : '#111827',
                  fontSize: '0.9rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginRight: '0.5rem'
                }}
              >
                {assigneesDisplayValue}
              </span>
              <i
                className={`pi pi-chevron-${assigneesPanelOpen ? 'up' : 'down'}`}
                style={{ fontSize: '0.9rem', color: '#6b7280' }}
              />
            </div>
            {assigneesPanelOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '0.25rem',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  maxHeight: '260px',
                  overflow: 'hidden',
                  zIndex: 1000,
                  boxShadow:
                    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                }}
              >
                <div
                  style={{
                    padding: '0.4rem 0.75rem',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <InputText
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    placeholder="Pretraži po imenu..."
                    style={{ width: '100%', fontSize: '0.85rem' }}
                  />
                </div>
                <div style={{ maxHeight: '210px', overflowY: 'auto' }}>
                  {users.length === 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.85rem',
                      color: '#6b7280'
                    }}
                  >
                    Nema članova tima za ovaj projekat.
                  </div>
                  )}
                  {/* Select all */}
                  {users.length > 0 && (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.4rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        borderBottom: '1px solid #f3f4f6',
                        fontWeight: 500
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.assignees.length > 0 &&
                          formData.assignees.length === users.length
                        }
                        onChange={() => {
                          setFormData(prev => {
                            const allIds = users.map(u => u._id);
                            const allSelected =
                              prev.assignees.length === allIds.length &&
                              allIds.every(id => prev.assignees.includes(id));
                            return {
                              ...prev,
                              assignees: allSelected ? [] : allIds
                            };
                          });
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>Selektuj sve</span>
                    </label>
                  )}
                  {users
                    .filter(u => {
                      const term = assigneeSearch.trim().toLowerCase();
                      if (!term) return true;
                      return (
                        u.firstName.toLowerCase().includes(term) ||
                        u.lastName.toLowerCase().includes(term) ||
                        u.username.toLowerCase().includes(term)
                      );
                    })
                    .map(user => {
                  const checked = formData.assignees.includes(user._id);
                  return (
                    <label
                      key={user._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.4rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssignee(user._id)}
                        style={{ margin: 0 }}
                      />
                      <span>
                        {user.firstName} {user.lastName}
                      </span>
                    </label>
                  );
                })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label htmlFor="taskStatus" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Status
            </label>
            <Dropdown
              id="taskStatus"
              value={formData.status}
              options={statusOptions}
              onChange={(e) => setFormData({ ...formData, status: e.value })}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label htmlFor="taskPriority" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Prioritet
            </label>
            <Dropdown
              id="taskPriority"
              value={formData.priority}
              options={priorityOptions}
              onChange={(e) => setFormData({ ...formData, priority: e.value })}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div>
          <label htmlFor="taskDeadline" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Rok
          </label>
          <Calendar
            id="taskDeadline"
            value={formData.deadline}
            onChange={(e) => setFormData({ ...formData, deadline: e.value as Date | null })}
            appendTo="self"
            panelClassName="compact-calendar"
            panelStyle={{ width: '100%' }}
            style={{ width: '100%' }}
            showIcon
            showTime
            dateFormat="dd.mm.yy"
          />
        </div>
        <div>
          <label htmlFor="taskEstimatedHours" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Procenjeno vreme (sati)
          </label>
          <InputText
            id="taskEstimatedHours"
            type="number"
            value={formData.estimatedHours.toString()}
            onChange={(e) => setFormData({ ...formData, estimatedHours: parseInt(e.target.value) || 0 })}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label htmlFor="taskTags" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Oznake
          </label>
          <InputText
            id="taskTags"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagKeyPress}
            style={{ width: '100%' }}
            placeholder="Dodaj oznake i pritisni Enter..."
          />
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {formData.tags.map((tag, index) => (
              <div key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <Tag
                  value={tag}
                  style={{ margin: 0 }}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-rounded p-button-text p-button-sm"
                  onClick={() => removeTag(tag)}
                  style={{ padding: '0.25rem', minWidth: 'auto', height: 'auto' }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TaskModal;

